import React, { useEffect, useState } from "react";
import { Badge, Container, Nav, Navbar, Offcanvas, Image, Spinner, Button, ListGroup } from "react-bootstrap";
import logo from "../assets/img/Logo_Izzi.svg";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {API_BASE} from "../services/apirest";
import { fetchSolicitudesCached } from "../services/solicitudesCache";

// =========================
// Helpers & Test Cases
// =========================
export function computeMyNoVisto(cAltas = 0, cMods = 0, cBajas = 0, cReacts = 0) {
    const a = Number(cAltas)  || 0;
    const m = Number(cMods)   || 0;
    const b = Number(cBajas)  || 0;
    const r = Number(cReacts) || 0;
    return a + m + b + r;
}

try {
    console.assert(computeMyNoVisto(1, 2, 3, 4) === 10, "computeMyNoVisto: 1+2+3+4 should be 10");
    console.assert(computeMyNoVisto(0, 0, 0, 0) === 0,  "computeMyNoVisto: zeros should be 0");
    console.assert(computeMyNoVisto("2", null, undefined, "3") === 5, "computeMyNoVisto: coerces inputs to numbers");
    console.assert(computeMyNoVisto("abc", 1, 1, 1) === 3, "computeMyNoVisto: non-numeric coerces to 0");
    console.assert(computeMyNoVisto("1", "2", "0", "0.5") === 3.5, "computeMyNoVisto: handles string numbers/decimals");
} catch (_) {}

// =========================
// Menú estático
// =========================
const NAV_ITEMS = [
    { href: "/periodico-digital", label: "Periódico Digital", rolesAllowed: [1,2,3,4,5,13,14] },
    { href: "/reportes",          label: "Reportes",          rolesAllowed: [1,2,3,13,14] },
    { href: "/mercadotecnia",     label: "Mercadotecnia",     rolesAllowed: [1,2,3,4,5,13,14] },
    { href: "/lineamientos",      label: "Lineamientos",      rolesAllowed: [1,2,3,4,13,14] },
    { href: "/Fabrizzio",         label: "Fabrizzio",         rolesAllowed: [1] },
];

export const NavBar = () => {
    const navigate = useNavigate();
    const [activeLink, setActiveLink] = useState("home");
    const [scrolled, setScrolled] = useState(false);
    const [navbarExpanded, setNavbarExpanded] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);
    const [rolId, setRolId] = useState(null);
    const [showProfile, setShowProfile] = useState(false);
    const [profile, setProfile] = useState(null);

    const [countPendiente, setCountPendiente] = useState(0);
    const [countEnProceso, setCountEnProceso] = useState(0);
    const [countModificaciones, setCountModificaciones] = useState(0);
    const [countModificacionesRol1, setCountModificacionesRol1] = useState(0);
    const [countReactivacion, setCountReactivacion] = useState(0);
    const [countBaja, setCountBaja] = useState(0);
    const [countBajasRol1, setCountBajasRol1] = useState(0);
    const [countAltas, setCountAltas] = useState(0);

    // Counters para rol 4 (mis ...)
    const [countMyAltas, setCountMyAltas]   = useState(0);
    const [countMyMods, setCountMyMods]     = useState(0);
    const [countMyBajas, setCountMyBajas]   = useState(0);
    const [countMyReacts, setCountMyReacts] = useState(0);
    const [refreshTick, setRefreshTick]     = useState(0);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const handler = () => setRefreshTick(prev => prev + 1);
        window.addEventListener("solicitudes:refresh", handler);
        return () => window.removeEventListener("solicitudes:refresh", handler);
    }, []);

    // helper
    const clearRole4Counters = React.useCallback(() => {
        setCountMyAltas(0);
        setCountMyMods(0);
        setCountMyBajas(0);
        setCountMyReacts(0);
    }, []);

    const countMyNoVisto = React.useMemo(
        () => computeMyNoVisto(countMyAltas, countMyMods, countMyBajas, countMyReacts),
        [countMyAltas, countMyMods, countMyBajas, countMyReacts]
    );

    // Normalizadores & filtros
    const normalizeStr = (v) => (v == null ? "" : String(v).trim().toLowerCase());
    const toNumOrNull = (v) => {
        if (v == null || v === "") return null;
        const n = Number(String(v).replace(/[^0-9.-]/g, ""));
        return Number.isFinite(n) ? n : null;
    };
    const first = (...cands) => cands.find(v => v !== undefined && v !== null && v !== "");
    // const norm = (v) => (v ? String(v).trim().toLowerCase() : "");

    const applyRoleFilter = React.useCallback((list = []) => {
        if (!profile) return list;

        if (rolId === 3) {
            const mySup = normalizeStr(first(
                profile.correo_supervisor,
                profile.Usuario,
                profile.usuario,
                profile.email
            ));
            return (list || []).filter(s => {
                const sup = normalizeStr(first(
                    s.correo_supervisor,
                    s.supervisor_correo,
                    s.supervisor_email
                ));
                return sup && mySup && sup === mySup;
            });
        }

        if (rolId === 2) {
            const myDistN = toNumOrNull(profile.distribuidor_id);
            const myDistS = normalizeStr(profile.distribuidor_id);
            return (list || []).filter(s => {
                const distN = toNumOrNull(s.distribuidor_id);
                if (myDistN != null && distN != null) return distN === myDistN;
                return normalizeStr(s.distribuidor_id) === myDistS;
            });
        }

        if (rolId === 4) {
            const me = normalizeStr(first(profile.email, profile.usuario_email));
            return (list || []).filter(s => {
                const creador = normalizeStr(first(s.creado_por, s.creadoPor, s.usuario_creador));
                return creador === me;
            });
        }

        // rol 1 (u otros): sin filtro
        return list;
    }, [profile, rolId]);

    // Efecto scroll
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Obtener rol del usuario
    useEffect(() => {
        const storedRolId = localStorage.getItem("rol_id");
        setRolId(parseInt(storedRolId, 10));
    }, []);

    // Cargar perfil simple (inicial)
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            setProfile(null);
            return;
        }

        fetch(`${API_BASE}/perfil.php`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        })
            .then(r => {
                if (!r.ok) throw new Error("Unauthorized");
                return r.json();
            })
            .then(d => {
                if (d.status === "success") {
                    setProfile(d.result.user);
                } else {
                    setProfile(null);
                }
            })
            .catch(() => {
                setProfile(null);
            });
    }, []);

    // helper: null/undefined/"" => true
    const isNullish = (v) => v == null || (typeof v === "string" && v.trim() === "");

    const dedupeSolicitudes = (arr) => {
        const keys = ["solicitud_id","SolicitudId","folio","Folio","folio_solicitud","FolioSolicitud"];
        const seen = new Set();
        const out  = [];
        for (const row of Array.isArray(arr) ? arr : []) {
            const rawKey = keys.map(k => row?.[k]).find(v => v != null && v !== "");
            if (!rawKey) { out.push(row); continue; }
            const key = String(rawKey).trim().toLowerCase();
            if (!seen.has(key)) { seen.add(key); out.push(row); }
        }
        return out;
    };
    // Helpers para deduplicar por "solicitud" aunque el API no traiga la misma clave
    // const getPath = (obj, path) => {
    //     try { return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj); } catch { return undefined; }
    // };
    // const prefer = (...cands) => cands.find(v => v !== undefined && v !== null && v !== "");
    // const clean  = (v) => (v == null ? "" : String(v).trim().toLowerCase());
    // const digits = (v) => (v == null ? "" : String(v).replace(/\D+/g, ""));

    // /** Construye una llave canónica de solicitud con varios "plan B" */
    // const makeSolicitudKey = (row) => {
    //     // 1) IDs “buenos”
    //     const id = prefer(row.solicitud_id, row.SolicitudId, row.solicitudId, getPath(row,"solicitud.id"));
    //     if (id != null) return `id:${id}`;
    //
    //     // 2) Folio normalizado (solo dígitos si existen)
    //     const folio = prefer(row.folio, row.Folio, row.folio_solicitud, row.FolioSolicitud, row.folioBase, row.FolioBase);
    //     if (folio) {
    //         const d = digits(folio);
    //         return d ? `folio:${d}` : `folio:${clean(folio)}`;
    //     }
    //
    //     // 3) Llave compuesta "tipo|creador|solicitante"
    //     const tipo  = clean(prefer(row.tipo, row.Tipo, row.tipo_solicitud, row.TipoSolicitud));
    //     const cread = clean(prefer(row.creado_por, row.creadoPor, row.usuario_creador, getPath(row,"creador.email")));
    //     const soli  = clean(prefer(row.solicitante_id, getPath(row,"solicitante.id"), row.UsuarioId, row.usuario_id));
    //     const comp  = [tipo, cread, soli].join("|");
    //     if (comp !== "||") return `comp:${comp}`;
    //
    //     // 4) Último recurso: JSON ordenado (estable)
    //     const ordered = Object.keys(row || {}).sort().reduce((acc,k)=>{acc[k]=row[k];return acc;}, {});
    //     return `json:${JSON.stringify(ordered)}`;
    // };

    // const dedupeSolicitudesSmart = (list) => {
    //     const seen = new Set(); const out = [];
    //     for (const r of (Array.isArray(list) ? list : [])) {
    //         const k = makeSolicitudKey(r);
    //         if (!seen.has(k)) { seen.add(k); out.push(r); }
    //     }
    //     return out;
    // };


    // ----- Utilidades fetch -----
    const fetchList = React.useCallback((url) =>
        fetchSolicitudesCached(url, { headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`
        } })
            .then(d => Array.isArray(d?.result?.solicitudes) ? d.result.solicitudes : [])
            .catch(() => []), []);

    const dedupeBy = React.useCallback((arr, keys = ["solicitud_id", "SolicitudId", "id"]) => {
        const seen = new Set();
        const out  = [];
        for (const x of (Array.isArray(arr) ? arr : [])) {
            const key = keys.map(k => x?.[k]).find(v => v !== undefined && v !== null) ?? JSON.stringify(x);
            if (!seen.has(key)) { seen.add(key); out.push(x); }
        }
        return out;
    }, []);

    // ----- Contadores generales por rol -----
    // Pendiente (rol 3 usa agregador)
    useEffect(() => {
        if (!profile || Number(rolId) !== 3) return;

        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` };

        // const normalizeStage = (val = "") =>
        //     String(val ?? "")
        //         .normalize("NFD")
        //         .replace(/\p{Diacritic}/gu, "")
        //         .trim()
        //         .toLowerCase();

        // const stageOf = (row, keyBase) => {
        //     if (keyBase === "baja") return normalizeStage(row?.baja_stage ?? row?.estado_baja ?? row?.stage);
        //     if (keyBase === "mods") return normalizeStage(row?.modificaciones_stage ?? row?.modificacion_stage ?? row?.estado_modificacion ?? row?.stage);
        //     if (keyBase === "react") return normalizeStage(row?.reactivacion_stage ?? row?.estado_reactivacion ?? row?.stage);
        //     return "";
        // };

        fetchSolicitudesCached(`${API_BASE}/solicitudes.php`, { headers })
            .then(d => {
                const arr = Array.isArray(d?.result?.solicitudes) ? d.result.solicitudes : [];
                const byRole = applyRoleFilter(arr);


                const basePend = byRole.filter(s => {
                    const baja  = s.baja_stage ?? s.estado_baja ?? s.stage_baja;
                    const react = s.reactivacion_stage ?? s.estado_reactivacion ?? s.stage_reactivacion;
                    const mods  = s.modificaciones_stage ?? s.modificacion_stage ?? s.estado_modificacion ?? s.stage_modificacion;
                    const modData =
                        s?.modificaciones_data ??
                        s?.modificacion_data ??
                        s?.data_modificacion ??
                        s?.datos_modificacion ??
                        s?.mod_data ??
                        s?.data ?? null;
                    return isNullish(baja) && isNullish(react) && isNullish(mods) && isNullish(modData);
                });

                setCountPendiente(basePend.length || 0);
            })
            .catch(err => {
                console.error("[rol3 aggregator] error", err);
                setCountPendiente(0);
            });
    }, [rolId, profile, refreshTick, applyRoleFilter]);

    // En proceso
    useEffect(() => {
        if (!profile || ![1, 2].includes(Number(rolId))) return;

        fetchSolicitudesCached(`${API_BASE}/solicitudes.php?Estado=${encodeURIComponent("En proceso")}`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
            .then(d => {
                const arr = Array.isArray(d?.result?.solicitudes) ? d.result.solicitudes : [];

                // descarta si pertenece a otros flujos (baja/react/mods) o trae data de modificación
                const onlyBaseEnProceso = arr.filter(s => {
                    const baja  = s.baja_stage ?? s.estado_baja ?? s.stage_baja;
                    const react = s.reactivacion_stage ?? s.estado_reactivacion ?? s.stage_reactivacion;
                    const mods  = s.modificaciones_stage ?? s.modificacion_stage ?? s.estado_modificacion ?? s.stage_modificacion;
                    const modData = s.modificaciones_data ?? s.modificacion_data ?? s.data_modificacion ?? s.datos_modificacion ?? s.mod_data ?? s.data ?? null;
                    return isNullish(baja) && isNullish(react) && isNullish(mods) && isNullish(modData);
                });

                //  Para ROL 2 NO aplicar filtro por distribuidor
                const list = (rolId === 2)
                    ? onlyBaseEnProceso
                    : applyRoleFilter(onlyBaseEnProceso);

                setCountEnProceso(dedupeSolicitudes(list).length || 0);
            })
            .catch(() => setCountEnProceso(0));
    }, [rolId, profile, refreshTick, applyRoleFilter]);


    // Altas = Registrando
    useEffect(() => {
        if (!profile || ![1, 14].includes(Number(rolId))) return;
        fetchSolicitudesCached(`${API_BASE}/solicitudes.php?Estado=${encodeURIComponent("Registrando")}`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
            .then(d => setCountAltas(applyRoleFilter(d.result?.solicitudes).length || 0))
            .catch(() => setCountAltas(0));
    }, [rolId, profile, refreshTick, applyRoleFilter]);

    // Modificaciones (rol 3 ahora se calcula en el agregador anterior)

    // Modificaciones pendientes supervisor (rol 3)
    useEffect(() => {
        if (!profile || Number(rolId) !== 3) return;

        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` };
        const normalizeStage = (s = "") =>
            String(s ?? "")
                .normalize("NFD")
                .replace(/\p{Diacritic}/gu, "")
                .trim()
                .toLowerCase();

        const hasNonNullData = (row) => {
            const v =
                row?.modificaciones_data ??
                row?.modificacion_data ??
                row?.data_modificacion ??
                row?.datos_modificacion ??
                row?.mod_data ??
                row?.data ?? null;
            return v != null;
        };

        fetchSolicitudesCached(`${API_BASE}/solicitudes.php?modificacion=${encodeURIComponent("pendientes")}`, { headers })
            .then(d => {
                const arr = Array.isArray(d?.result?.solicitudes) ? d.result.solicitudes : [];
                const byRole = applyRoleFilter(arr);

                const matches = dedupeSolicitudes(byRole).filter(row =>
                    normalizeStage(row?.modificaciones_stage ?? row?.modificacion_stage ?? row?.estado_modificacion ?? row?.stage) === "solicitudmodificacion" &&
                    hasNonNullData(row)
                );

                console.log("matches modificaciones", matches);

                setCountModificaciones(matches.length || 0);
            })
            .catch(err => {
                console.error("[rol3 mods] error", err);
                setCountModificaciones(0);
            });
    }, [rolId, profile, refreshTick, applyRoleFilter]);

    // Bajas pendientes supervisor (rol 3)
    useEffect(() => {
        if (!profile || Number(rolId) !== 3) return;

        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` };
        const normalizeStage = (s = "") =>
            String(s ?? "")
                .normalize("NFD")
                .replace(/\p{Diacritic}/gu, "")
                .trim()
                .toLowerCase();

        fetchSolicitudesCached(`${API_BASE}/solicitudes.php?baja_stage=${encodeURIComponent("SolicitudBaja")}`, { headers })
            .then(d => {
                const arr = Array.isArray(d?.result?.solicitudes) ? d.result.solicitudes : [];
                const byRole = applyRoleFilter(arr);
                const matches = dedupeSolicitudes(byRole).filter(row =>
                    normalizeStage(row?.baja_stage ?? row?.estado_baja ?? row?.stage) === "solicitudbaja"
                );
                setCountBaja(matches.length || 0);
            })
            .catch(err => {
                console.error("[rol3 bajas] error", err);
                setCountBaja(0);
            });
    }, [rolId, profile, refreshTick, applyRoleFilter]);

    // Modificaciones rol 1 (por ejecutar / aprobadas para ejecución)
    useEffect(() => {
        if (!profile || ![1, 14].includes(Number(rolId))) return;

        // 1) Fuente correcta para rol 1
        const primaryUrl = `${API_BASE}/solicitudes.php?modificacion=por_ejecutar`;
        const fallbackUrl = `${API_BASE}/solicitudes.php?modificaciones_stage=${encodeURIComponent("Aprobada")}`;

        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` };
        const norm = (v) => (v == null ? "" : String(v).trim().toLowerCase());
        const stageOf = (row) => norm(row.modificaciones_stage ?? row.modificacion_stage ?? row.estado_modificacion ?? row.stage);

        // Deduplicar SOLO por SolicitudId/solicitud_id
        const dedupeBySolicitud = (arr) => {
            const seen = new Set(); const out = [];
            for (const r of Array.isArray(arr) ? arr : []) {
                const key = (r.SolicitudId ?? r.solicitud_id ?? null);
                const k = key == null ? null : String(key).trim();
                if (k && !seen.has(k)) { seen.add(k); out.push(r); }
                if (k == null) out.push(r); // si no trae id, no lo descartes
            }
            return out;
        };

        const fetchJson = (url) =>
            fetchSolicitudesCached(url, { headers }).catch(() => null);

        (async () => {
            // 2) Trae del endpoint primario; si falla, usa fallback
            let data = await fetchJson(primaryUrl);
            if (!data || !Array.isArray(data?.result?.solicitudes)) {
                console.warn("[rol1] primary failed, using fallback", primaryUrl);
                data = await fetchJson(fallbackUrl);
            }

            const raw = Array.isArray(data?.result?.solicitudes) ? data.result.solicitudes : [];
            // 3) Filtro defensivo por stage aprobada (aunque por_ejecutar ya viene filtrado)
            const filtered = raw.filter(r => stageOf(r) === "aprobada" || data?.from === "por_ejecutar");

            // 4) Dedup estricto por SolicitudId
            const list = dedupeBySolicitud(filtered);

            // 5) (Opcional) histograma de stages para debug
            const hist = list.reduce((acc, r) => { const k = stageOf(r) || "(vacío)"; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
            console.log("[rol1] mods raw:", raw.length, "filtradas:", filtered.length, "dedupe:", list.length, "por stage:", hist);

            // 6) Para rol 1 applyRoleFilter no filtra, pero lo dejamos por consistencia
            setCountModificacionesRol1(applyRoleFilter(list).length || 0);
        })();
    }, [rolId, profile, refreshTick, applyRoleFilter]);


    // Reactivación en solicitud
    useEffect(() => {
        if (!profile || ![1, 14].includes(Number(rolId))) return;
        fetchSolicitudesCached(`${API_BASE}/solicitudes.php?reactivacion_stage=${encodeURIComponent("SolicitudReactivacion")}`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
            .then(d => setCountReactivacion(applyRoleFilter(d.result?.solicitudes).length || 0))
            .catch(() => setCountReactivacion(0));
    }, [rolId, profile, refreshTick, applyRoleFilter]);

    // Bajas rol 1 (CapturaBaja)
    useEffect(() => {
        if (!profile || ![1, 14].includes(Number(rolId))) return;
        const url = `${API_BASE}/solicitudes.php?baja_stage=${encodeURIComponent("PROCESS")}`;
        console.log("[rol1] GET bajas", url);
        fetchSolicitudesCached(url, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
            .then(d => {
                const arr = Array.isArray(d?.result?.solicitudes) ? d.result.solicitudes : [];
                const list = dedupeBy(arr, ["solicitud_id","SolicitudId","id","folio","Folio"]);
                const norm = (v) => (v == null ? "" : String(v).trim().toLowerCase());
                const stageOf = (row) => norm(row.baja_stage ?? row.estado_baja ?? row.stage);
                const filtered = list.filter((row) => ["capturabaja", "solicitudbaja"].includes(stageOf(row)));
                setCountBajasRol1(applyRoleFilter(filtered).length || 0);
            })
            .catch(() => setCountBajasRol1(0));
    }, [rolId, profile, refreshTick, applyRoleFilter, dedupeBy]);


// =========================
// Rol 4: contadores "mis ..." (reglas estrictas y match por distribuidor)
// =========================
    const refreshRole4Counters = React.useCallback(async () => {
        if (rolId !== 4 || !profile) return;

        const toNum = (v) => {
            if (v == null || v === '') return null;
            const n = Number(String(v).replace(/[^0-9.-]/g, ''));
            return Number.isFinite(n) ? n : null;
        };
        const norm = (v) => (v == null ? "" : String(v).trim().toLowerCase());

        const sessionDistribId = toNum(profile?.distribuidor_id);

        const matchesRol4Owner = (row) => {
            const rowDistId = toNum(first(row?.distribuidor_id, row?.id_distribuidor));
            if (sessionDistribId != null && rowDistId != null) {
                return sessionDistribId === rowDistId;
            }
            return false;
        };

        try {
            const stageOf = (row, keyBase) => {
                const raw = String(
                    keyBase === "baja"
                        ? (row?.baja_stage ?? row?.estado_baja ?? row?.stage ?? row?.bajaStage)
                        : keyBase === "react"
                            ? (row?.reactivacion_stage ?? row?.estado_reactivacion ?? row?.stage ?? row?.reactivacionStage)
                            : /* mods */
                              (row?.modificaciones_stage ?? row?.modificacionesStage ??
                               row?.modificacion_stage ?? row?.modificacionStage ??
                               row?.estado_modificacion ?? row?.estadoModificacion ??
                               row?.stage_modificacion ?? row?.stageModificacion ??
                               row?.mod_stage ?? row?.modStage ??
                               row?.stage)
                ) || "";
                return raw.normalize("NFD").replace(/\p{Diacritic}/gu, "").trim().toLowerCase();
            };

            const modsData = (row) =>
                row?.modificaciones_data ??
                row?.modificacion_data ??
                row?.data_modificacion ??
                row?.datos_modificacion ??
                row?.mod_data ??
                row?.data ?? null;

            const isNullStrict = (v) => v == null;

            const [uCompletado, uModsEjecutada, uBajaEjec, uReactEjec] = await Promise.all([
                fetchList(`${API_BASE}/solicitudes.php?Estado=${encodeURIComponent('Completado')}`),
                fetchList(`${API_BASE}/solicitudes.php?modificaciones_stage=${encodeURIComponent('Ejecutada')}`),
                fetchList(`${API_BASE}/solicitudes.php?baja_stage=${encodeURIComponent('Ejecutada')}`),
                fetchList(`${API_BASE}/solicitudes.php?reactivacion_stage=${encodeURIComponent('Ejecutada')}`)
            ]);

            const dCompletado = dedupeBy(uCompletado);
            const dModsExec   = dedupeBy(uModsEjecutada);
            const dBajaExec   = dedupeBy(uBajaEjec);
            const dReactExec  = dedupeBy(uReactEjec);

            const myAltas = dCompletado.filter(row => {
                const okEstado     = norm(row?.Estado ?? row?.estado) === "completado";
                const bajaNull     = isNullStrict(row?.baja_stage);
                const reactNull    = isNullStrict(row?.reactivacion_stage);
                const modStageNull = isNullStrict(row?.modificaciones_stage);
                const modDataNull  = isNullStrict(modsData(row));
                return okEstado && bajaNull && reactNull && modStageNull && modDataNull && matchesRol4Owner(row);
            });

            const myMods = dModsExec.filter(row => {
                const statusOk = norm(row?.status ?? row?.Status) === "activo";
                return statusOk && matchesRol4Owner(row);
            });

            const myBajas = dBajaExec.filter(row =>
                stageOf(row, "baja") === "ejecutada" &&
                matchesRol4Owner(row)
            );

            const myReacts = dReactExec.filter(row =>
                stageOf(row, "react") === "ejecutada" &&
                matchesRol4Owner(row)
            );

            console.log("[ROL4 debug] fetched", {
                completado: dCompletado.length,
                modsEjec: dModsExec.length,
                bajaEjec: dBajaExec.length,
                reactEjec: dReactExec.length,
            });

            setCountMyAltas(myAltas.length);
            setCountMyMods(myMods.length);
            setCountMyBajas(myBajas.length);
            setCountMyReacts(myReacts.length);

            console.log("[ROL4] resultados (ejecutadas/completadas):", {
                myAltas: myAltas.length,
                myMods: myMods.length,
                myBajas: myBajas.length,
                myReacts: myReacts.length
            });
        } catch (e) {
            console.error('[rol4] refresh counters error', e);
            setCountMyAltas(0); setCountMyMods(0); setCountMyBajas(0); setCountMyReacts(0);
        }
    }, [rolId, profile, fetchList, dedupeBy]);

    // Evento externo para refrescar
    useEffect(() => {
        const onRefresh = () => { if (rolId === 4) refreshRole4Counters(); };
        window.addEventListener('inbox:refresh', onRefresh);
        return () => window.removeEventListener('inbox:refresh', onRefresh);
    }, [rolId, refreshRole4Counters]);

    // Refresco inicial rol 4
    useEffect(() => {
        if (rolId === 4 && profile) {
            (async () => { await refreshRole4Counters(); })();
        }
    }, [rolId, profile, refreshRole4Counters]);

    // Obtener perfil (al abrir offcanvas) con normalización de foto
    const fetchProfile = async () => {
        try {
            const res = await fetch(`${API_BASE}/perfil.php`, {
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            const { status, result } = await res.json();
            if (status === "success") {
                let user = result.user;
                if (user.foto_url) {
                    const filename = user.foto_url.replace(/\\/g,'/').split('/uploads/').pop();
                    user.foto_url = `${API_BASE}/uploads/${filename}`;
                }
                setProfile(user);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleLogout = async () => {
        localStorage.removeItem("token");
        localStorage.removeItem("rol_id");
        navigate("/login");
    };

    const toggleAccountMenu = () => setOpenMenu(openMenu === 'account' ? null : 'account');
    const toggleInboxMenu   = () => {
        setOpenMenu(openMenu === 'inbox' ? null : 'inbox');
        console.log(
            `Pendi:${countPendiente} Proceso:${countEnProceso} Altas:${countAltas} Modif:${countModificaciones} ModifR1:${countModificacionesRol1} React:${countReactivacion} Baja:${countBaja} Rol4:${countMyNoVisto}`
        );
    };

    const handlePerfilClick = () => {
        setOpenMenu(null);
        setShowProfile(true);
        if (!profile) fetchProfile();
    };

    const handleConfigClick = () => {
        if ([1,2,3,13,14].includes(rolId)) {
            setNavbarExpanded(false);
            navigate("/admin");
        } else {
            Swal.fire({ icon: "error", title: "Error", text: "No tienes acceso" });
        }
    };

    const visibleInboxCount = React.useMemo(() => {
        switch (Number(rolId)) {
            case 1: 
            case 14: return countAltas + countModificacionesRol1 + countReactivacion + countBajasRol1;
            case 2: return countEnProceso;
            case 3: return countPendiente + countModificaciones + countBaja;
            case 4: return countMyNoVisto;
            default: return 0;
        }
    }, [
        rolId,
        countAltas, countModificacionesRol1, countReactivacion, countBajasRol1,
        countEnProceso, countPendiente, countModificaciones, countBaja,
        countMyNoVisto
    ]);

    return (
        <>
            <Navbar collapseOnSelect expand="lg" expanded={navbarExpanded} onToggle={setNavbarExpanded} className={`navbar-custom ${scrolled ? "scrolled" : ""}`}>
                <Container>
                    <Navbar.Brand href="/">
                        <img src={logo} alt="Logo" />
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="basic-navbar-nav" />
                    <Navbar.Collapse id="basic-navbar-nav">
                        <Nav className="ms-auto align-items-center text-center">
                            {NAV_ITEMS.filter(({rolesAllowed}) => rolesAllowed.includes(rolId)).map(({href,label}) => {
                                const linkKey = label.toLowerCase().replace(/\s+/g, "-");
                                return (
                                    <Nav.Link
                                        key={href}
                                        href={href}
                                        className={activeLink === linkKey ? "active navbar-link" : "navbar-link"}
                                        onClick={() => setActiveLink(linkKey)}
                                    >
                                        {label}
                                    </Nav.Link>
                                );
                            })}

                            <div className="nav-buttons-container">
                                {/* Mi Cuenta */}
                                <button className="btn-account" onClick={() => {
                                    if (localStorage.getItem("token")) {
                                        toggleAccountMenu();
                                    } else {
                                        navigate("/login");
                                    }
                                }}>
                                    <i className="account-icon bi bi-person"></i>
                                    <span className="account-icon-text">
                                        {localStorage.getItem("token") ? "Mi cuenta" : "Iniciar sesión"}
                                    </span>
                                </button>
                                {openMenu === 'account' && (
                                    <ul className="navbar-dropdown-menu">
                                        <li><button onClick={() => {handlePerfilClick();}} className="config-button">Perfil</button></li>
                                        {[1, 13].includes(rolId) && (
                                            <li><button onClick={() => {handleConfigClick(); setOpenMenu(null);}} className="config-button">Configuración</button></li>
                                        )}
                                        {rolId === 1 && (
                                            <li><button onClick={() => {navigate("/admin-database"); setOpenMenu(null);}} className="config-button text-danger fw-bold">DB Maestro</button></li>
                                        )}
                                    </ul>
                                )}

                                {/* Inbox */}
                                <button className="btn-account position-relative" onClick={toggleInboxMenu}>
                                    <i className="account-icon bi bi-inbox ms-1 me-1"></i>
                                    {visibleInboxCount > 0 && (
                                        <Badge bg="danger" pill className="position-absolute top-0 start-100 translate-middle" style={{ fontSize: '0.65rem' }}>
                                            {visibleInboxCount}
                                        </Badge>
                                    )}
                                </button>

                                {openMenu === 'inbox' && (
                                    <ul className="navbar-dropdown-menu" style={{ minWidth: '220px' }}>
                                        {/* --- ROL 3 (Supervisor) --- */}
                                        {([3].includes(rolId)) && (
                                            <li style={{ cursor: 'pointer' }}
                                                onClick={() => { setOpenMenu(null); navigate('/solicitudes'); setActiveLink('solicitudes'); }}>
                                                Solicitudes Pendientes
                                                {countPendiente > 0 && (<Badge bg="danger" pill className="ms-2">{countPendiente}</Badge>)}
                                            </li>
                                        )}

                                        {/* --- ROL 1 y 14 (Admin/Analista) --- */}
                                        {(rolId === 1 || rolId === 14) && (
                                            <>
                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/registro'); setActiveLink('registro'); }}>
                                                    Solicitudes Altas
                                                    {countAltas > 0 && (<Badge bg="warning" pill className="ms-2">{countAltas}</Badge>)}
                                                </li>

                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/aprueba-modificacion-supervisor'); setActiveLink('aprueba-modificacion-supervisor'); }}>
                                                    Modificaciones
                                                    {countModificacionesRol1 > 0 && (<Badge bg="danger" pill className="ms-2">{countModificacionesRol1}</Badge>)}
                                                </li>

                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/solicitud-reactivar'); setActiveLink('solicitud-reactivar'); }}>
                                                    Solicitudes de Reactivación
                                                    {countReactivacion > 0 && (<Badge bg="danger" pill className="ms-2">{countReactivacion}</Badge>)}
                                                </li>

                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/AprobacionBaja'); setActiveLink('AprobacionBaja'); }}>
                                                    Folio Bajas
                                                    {countBajasRol1 > 0 && (<Badge bg="warning" pill className="ms-2">{countBajasRol1}</Badge>)}
                                                </li>

                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/gestion-bodegas'); setActiveLink('gestion-bodegas'); }}>
                                                    Gestión de Bodegas
                                                </li>
                                            </>
                                        )}

                                        {/* --- ROL 2 (Y ROL 1, 14) --- */}
                                        {([1, 2, 14].includes(rolId)) && (
                                            <li style={{ cursor: 'pointer' }}
                                                onClick={() => { setOpenMenu(null); navigate('/solicitudes-proceso'); setActiveLink('solicitudes-proceso'); }}>
                                                Solicitudes en Proceso
                                                {countEnProceso > 0 && (<Badge bg="warning" pill className="ms-2">{countEnProceso}</Badge>)}
                                            </li>
                                        )}

                                         {([1, 2, 14].includes(Number(rolId))) && (
                                            <li style={{ cursor: 'pointer' }}
                                                onClick={() => { 
                                                    setOpenMenu(null); 
                                                    navigate('/movimientos-solicitudes'); 
                                                    setActiveLink('movimientos-solicitudes'); 
                                                }}>
                                                Movimiento de solicitudes
                                            </li>
                                        )}

                                        {/* --- ROL 4 --- */}
                                        {rolId === 4 && (
                                            <>
                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/completas'); setActiveLink('solicitudes'); clearRole4Counters(); }}>
                                                    Solicitudes
                                                    {countMyNoVisto > 0 && (<Badge bg="danger" pill className="ms-2">{countMyNoVisto}</Badge>)}
                                                </li>

                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/solicitar-usuario'); setActiveLink('solicitudes'); }}>
                                                    Solicitar Usuario
                                                </li>
                                            </>
                                        )}

                                        {/* --- ROLES 1,2,3,4,14 --- */}
                                        {([1,2,3,4,14].includes(rolId)) && (
                                            <li style={{ cursor: 'pointer' }} onClick={() => { setOpenMenu(null); navigate('/plantilla'); setActiveLink('plantilla'); }}>
                                                Plantilla
                                            </li>
                                        )}

                                        {/* --- ROL 3 extras --- */}
                                        {rolId === 3 && (
                                            <>
                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/solicitud-modificacion-supervisor'); setActiveLink('solicitud-modificacion-supervisor'); }}>
                                                    Modificaciones Pendientes
                                                    {countModificaciones > 0 && (<Badge bg="danger" pill className="ms-2">{countModificaciones}</Badge>)}
                                                </li>
                                                <li style={{ cursor: 'pointer' }}
                                                    onClick={() => { setOpenMenu(null); navigate('/baja-proceso'); setActiveLink('baja-proceso'); }}>
                                                    Solicitudes de Baja
                                                    {countBaja > 0 && (<Badge bg="danger" pill className="ms-2">{countBaja}</Badge>)}
                                                </li>
                                            </>
                                        )}

                                        {/* --- Blacklist para todos los permitidos --- */}
                                        {([1, 14, 8, 4].includes(rolId)) && (
                                            <li style={{ cursor: 'pointer' }}
                                                onClick={() => { setOpenMenu(null); navigate('/blacklist'); setActiveLink('blacklist'); }}>
                                                Blacklist
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>

            {/* Panel lateral de perfil */}
            <Offcanvas
                show={showProfile}
                onHide={() => setShowProfile(false)}
                placement="end"
                container={typeof document !== 'undefined' ? document.body : undefined}
                style={{ zIndex: 11000, width: '320px' }}
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    {profile && (
                        <div className="d-flex align-items-center">
                            {profile.foto_url ? (
                                <Image src={profile.foto_url} roundedCircle width={60} height={60} style={{ objectFit: 'cover' }} className="me-3" />
                            ) : (
                                <i className="bi bi-person-circle me-3 text-secondary" style={{ fontSize: '2rem' }} />
                            )}
                            <div>
                                <h5 className="mb-0">
                                    {profile.nombre_usuario} {profile.apellido_paterno} {profile.apellido_materno}
                                </h5>
                            </div>
                        </div>
                    )}
                </Offcanvas.Header>

                <Offcanvas.Body className="pt-3">
                    {profile ? (
                        <>
                            <ListGroup variant="flush" className="mb-4">
                                <ListGroup.Item className="d-flex align-items-center">
                                    <i className="bi bi-envelope-fill me-2 text-primary" />
                                    <span>{profile.email}</span>
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center">
                                    <i className="bi bi-award-fill me-2 text-info" />
                                    <span>Puesto: {profile.rol_nombre}</span>
                                </ListGroup.Item>
                            </ListGroup>
                            <div className="d-grid gap-2">
                                <Button variant="danger" size="lg" onClick={handleLogout}>Cerrar Sesión</Button>
                            </div>
                        </>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5">
                            {!localStorage.getItem("token") ? (
                                <>
                                    <p className="text-muted mb-3">No has iniciado sesión</p>
                                    <Button variant="primary" onClick={() => navigate("/login")}>Ir al Login</Button>
                                </>
                            ) : (
                                <div className="text-center">
                                    <Spinner animation="border" className="mb-3" />
                                    <p className="text-muted">Cargando perfil...</p>
                                    <Button variant="outline-danger" className="mt-2" onClick={handleLogout}>Cerrar sesión actual</Button>
                                </div>
                            )}
                        </div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};
 
